import { Button, Stack, StackProps, useToast } from '@chakra-ui/react';
import { copyText } from '@pelicargo/ui';
import { formatAddress } from '@pelicargo/utils';
import { Quote } from '@prisma/client';
import { useFormContext } from 'react-hook-form';
import { HiOutlineClipboardCopy } from 'react-icons/hi';

import { trpc } from '../../config/trpc';
import { ContactInput } from '../Autocomplete/ContactInput';
import { LabeledText } from '../LabeledText';
import { SimpleLabeledPopoverAddressInput } from '../SimpleLabeledPopoverAddressInput';
import { SimpleLabeledPopoverDockHoursInput } from '../SimpleLabeledPopoverDockHoursInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { BookingSection } from './AfrBookingForm';

type Props = { quote: Quote } & StackProps;

export const FcaBookingForm = ({ quote, ...rest }: Props) => {
  const formContext = useFormContext();
  const deliveryAddress = formContext.watch('delivery_address');
  const toast = useToast();

  const { data: dropoff_contact } = trpc.getContact.useQuery({ id: quote?.dropoff_contact_id });

  const handleClick = async () => {
    const addressString = formatAddress(deliveryAddress);

    try {
      if (!addressString) throw new Error('No address to copy');

      copyText(addressString);
      toast({ title: 'Address copied!', status: 'success' });
    } catch (error) {
      toast({ title: 'Copy Failed', description: error?.message, status: 'error' });
    }
  };

  return (
    <Stack {...rest} spacing={6}>
      <Stack direction="row" flex={1} spacing={4}>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Booking Request Details">
            <SimpleLabeledPopoverInput
              name="dropoff_at"
              label="Drop-Off At"
              inputType="date-picker"
              initialTimeZone="Etc/UTC"
              showTime={false}
              isHorizontal
              showClearButton
            />
          </BookingSection>
          <BookingSection title="References">
            <SimpleLabeledPopoverInput isHorizontal name="pickup_ref" label="Pickup Ref #" />
            <SimpleLabeledPopoverInput isHorizontal name="trucking_ref" label="Trucking Ref #" />
            <SimpleLabeledPopoverInput isHorizontal name="customer_ref" label="Customer Ref #" />
          </BookingSection>
          <BookingSection title="Details">
            <SimpleLabeledPopoverInput
              isHorizontal
              name="booking_instructions"
              label="Booking details"
              inputType="textarea"
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="awb_instructions"
              label="AWB Instructions"
              inputType="textarea"
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="additional_information"
              label="Additional details"
              inputType="textarea"
            />
          </BookingSection>
        </Stack>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Drop-Off">
            <LabeledText
              label="Email"
              textProps={{ noOfLines: 1 }}
              value={dropoff_contact?.email || '-'}
              isCopyable
              isHorizontal
              py={1}
              px={2}
            />
            <LabeledText
              label="Phone"
              textProps={{ noOfLines: 1 }}
              value={dropoff_contact?.phone_number || '-'}
              isHorizontal
              py={1}
              px={2}
            />
            <SimpleLabeledPopoverInput
              inputType="textarea"
              name="dropoff_instructions"
              label="Drop-Off details"
              isHorizontal
            />
            <SimpleLabeledPopoverDockHoursInput />
            <SimpleLabeledPopoverAddressInput name="delivery_address" />
            <Button
              variant="ghost"
              size="xs"
              colorScheme="blue"
              leftIcon={<HiOutlineClipboardCopy />}
              onClick={handleClick}
              alignSelf="flex-start"
            >
              Copy Address
            </Button>
            <Stack mt={2}>
              <ContactInput
                name="dropoff_contact_id"
                label="Drop-Off Contact"
                defaultAirlineIds={[quote?.provider_id]}
                defaultAirportIds={[quote?.origin_airport_id]}
                my={1}
                mx={2}
              />
            </Stack>
          </BookingSection>
        </Stack>
      </Stack>
    </Stack>
  );
};
